import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useEndpoints } from "../../api/ApiContext";
import type { CloseBreakerResponse, TripBreakerResponse } from "../../api/staffTypes";
import { Banner, Button, Card, Dialog, PageHeader, QueryView, StatusPill, Textarea } from "../../components/ui";
import { AwaitingSecondBanner, reasonError, StaffRejection, StepUpNote, useStaffCommand, when } from "./staffCommon";

// The CPE circuit breaker (ARCHITECTURE.md §7; migrations 0020, 0029,
// 0068, 0073; breaker.go): the manual trip and close. Migration 0068's
// §5 tighten/loosen asymmetry: trip stays single-reviewer and immediate
// (a protective action); close now goes through request_breaker_close —
// a recorded reason, an impact preview (how many live CPE campaigns it
// resumes) and dual approval by two distinct reviewers — before the
// underlying close_circuit_breaker (0020) ever runs. Both need a recent
// MFA step-up and are idempotent. GET /v1/internal/circuit-breaker
// (G-52) is the detail read: version, transition history, pending close
// votes and the impact preview, plus the real half-open state migration
// 0029 already tracks — backoff_level, half_open_not_before and the
// current probe generation's in-flight count (0073 fixed these from
// 0068's hardcoded null/0; they're only non-null/non-zero while the
// breaker is actually half_open).

export function BreakerPage() {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ["breaker-detail"], queryFn: () => ep.staff.breaker.get() });
  function refresh() {
    void qc.invalidateQueries({ queryKey: ["breaker-detail"] });
    void qc.invalidateQueries({ queryKey: ["system-status"] });
  }
  return (
    <div className="stack-lg">
      <PageHeader title="CPE circuit breaker" description="Stops CPE decisioning network-wide. Opening (trip) tightens control and is immediate; closing resumes traffic and is dual-controlled." />
      <Card title="Current state">
        <QueryView query={detail} loadingLabel="Reading the breaker's state">
          {(d) => (
            <div className="stack-sm">
              <p className="row">
                <StatusPill tone={d.state === "closed" ? "success" : d.state === "open" ? "error" : "warning"}>{d.state.replace("_", "-")}</StatusPill>
                <span className="small muted">version {d.version}</span>
                {d.transitioned_at ? <span className="small muted">since {when(d.transitioned_at)}</span> : null}
              </p>
              {d.state === "open" && d.pending_close_approvals.length ? (
                <p className="small">
                  Close votes so far: {d.pending_close_approvals.length} of 2 — {d.pending_close_approvals.map((a) => a.actor_user_id.slice(0, 8)).join(", ")}
                </p>
              ) : null}
              {d.backoff_level !== null && (d.backoff_level > 0 || d.half_open_not_before) ? (
                <p className="small">
                  Backoff level {d.backoff_level}
                  {d.half_open_not_before ? <> — half-open not before {when(d.half_open_not_before)}</> : null}
                </p>
              ) : null}
              {d.state === "half_open" ? (
                <p className="small">
                  Probing generation {d.probes.generation} — {d.probes.in_flight} probe{d.probes.in_flight === 1 ? "" : "s"} in flight.
                </p>
              ) : null}
              <p className="small">Closing now would resume decisioning for {d.impact_of_close.cpe_campaigns_affected} live CPE campaign(s).</p>
            </div>
          )}
        </QueryView>
      </Card>
      <BreakerAction action="trip" onDone={refresh} />
      <BreakerAction action="close" onDone={refresh} approvalsSoFar={detail.data?.pending_close_approvals.length} />
    </div>
  );
}

function BreakerAction({ action, onDone, approvalsSoFar }: { action: "trip" | "close"; onDone: () => void; approvalsSoFar?: number }) {
  const ep = useEndpoints();
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const cmd = useStaffCommand<{ reason: string }, TripBreakerResponse | CloseBreakerResponse>(
    (req: { reason: string }, opts) => (action === "trip" ? ep.staff.breaker.trip(req.reason, opts) : ep.staff.breaker.close(req.reason, opts)),
    () => {
      setConfirming(false);
      setReason("");
      onDone();
    },
  );
  function submit(e: FormEvent) {
    e.preventDefault();
    const re = reasonError(reason);
    if (re) return setFormError(re);
    setFormError(null);
    cmd.reset();
    setConfirming(true);
  }
  const res = cmd.data;
  const title = action === "trip" ? "Open (trip) the breaker" : "Close the breaker";
  const closeAwaiting = action === "close" && res && "result" in res && res.result === "awaiting_second_approval";
  return (
    <Card title={title}>
      {res && !closeAwaiting ? (
        <Banner tone={res.applied ? "success" : "info"} live="polite" title={res.result.replace(/_/g, " ")}>
          Version {res.version}
          {"backoff_level" in res && res.backoff_level !== undefined ? `, backoff level ${res.backoff_level}` : ""}
          {"half_open_not_before" in res && res.half_open_not_before ? `, half-open not before ${when(res.half_open_not_before)}` : ""}
          {"impact_preview" in res && res.impact_preview ? `. Resumed decisioning for ${res.impact_preview.cpe_campaigns_affected} CPE campaign(s).` : "."}
        </Banner>
      ) : null}
      {closeAwaiting && res && "approvals" in res ? <AwaitingSecondBanner approvals={res.approvals} required={res.approvals_required} /> : null}
      <form className="stack" onSubmit={submit} noValidate>
        <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        <StepUpNote required />
        {action === "close" ? (
          <p className="xs muted">
            Dual-controlled: needs two distinct reviewers, keyed to this open episode's version.
            {typeof approvalsSoFar === "number" && approvalsSoFar > 0 ? ` ${approvalsSoFar} of 2 votes recorded so far.` : ""}
          </p>
        ) : null}
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error && !confirming ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" variant={action === "trip" ? "danger" : "secondary"}>
            {action === "trip" ? "Trip…" : "Close…"}
          </Button>
        </div>
      </form>
      <Dialog
        open={confirming}
        alert
        onClose={() => setConfirming(false)}
        dismissible={!cmd.isPending}
        title={action === "trip" ? "Trip the breaker?" : "Close the breaker?"}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={cmd.isPending}>
              Cancel
            </Button>
            <Button variant="danger" loading={cmd.isPending} onClick={() => cmd.run({ reason: reason.trim() })}>
              {action === "trip" ? "Trip breaker" : "Close breaker"}
            </Button>
          </>
        }
      >
        <div className="stack-sm small">
          <span>
            {action === "trip"
              ? "CPE decisioning stops for every publisher until the breaker recovers or is closed."
              : "Your vote toward closing is recorded now; CPE decisioning only resumes once a second, distinct reviewer also votes to close."}
          </span>
          {cmd.error ? <StaffRejection error={cmd.error} /> : null}
        </div>
      </Dialog>
    </Card>
  );
}
